import { HTMLAttributes, useEffect, useRef, useState } from 'react';
import { useBasicBarChart } from '@/dashboard/components/echarts/hooks/useBasicBarChart';
import LogoLoading from '@/dashboard/components/loading/LogoLoading';
import { useMaterialMedia } from '@/dashboard/hooks/useMaterialMedia';
import { usePolicyStore } from '@/dashboard/store/PolicyStore';
import { globalDataStore } from '@/dashboard/store/globalDataStore';
import {
  InsuranceType,
  transformDateStrToTimeString,
} from '@jove-frontend/uikit';
import { useWindowSize } from '@jove-frontend/uikit';
import { Box, Typography } from '@mui/material';
import clsx from 'clsx';
import { isArray, max } from 'lodash';

type PaymentScheduleProps = HTMLAttributes<HTMLDivElement>;

const PaymentSchedule = (props: PaymentScheduleProps) => {
  const { className } = props;
  const [
    { paymentLoading, paymentScheduleInfo, policies },
    { updatePaymentAllInfo },
  ] = usePolicyStore((store) => [store.state, store.actions]);
  const chartRef = useRef(null);
  const { drawChart, generateChartInstance, drawStackedColumnChart } =
    useBasicBarChart();
  const [year, setYear] = useState<number>(
    Number(transformDateStrToTimeString(new Date(), { format: 'YYYY' }))
  );
  const windowSize = useWindowSize();
  const { isMobile } = useMaterialMedia();
  const {
    state: { queryParams },
  } = globalDataStore();

  const roundToNearest = (value) => {
    if (value < 10) {
      return 10;
    }
    const exponent = Math.floor(Math.log10(value));
    const base = Math.pow(10, exponent);
    return Number(Math.ceil(value / base) * base);
  };

  /**
   * effects
   */
  useEffect(() => {
    if (isArray(policies) && policies.length > 0) {
      updatePaymentAllInfo({ year, queryUserId: queryParams?.queryUserId });
    }
  }, [policies]);

  useEffect(() => {
    if (chartRef.current && chartRef.current.resize) chartRef.current.resize();
  }, [windowSize]);

  // useEffect(() => {
  //   if (paymentScheduleInfo) {
  //     // Temporary title code, to be removed
  //     let temporaryTitleList = [];
  //     policies.forEach((item) => {
  //       if (!temporaryTitleList.includes(item.quotePayload?.insuranceType)) {
  //         temporaryTitleList.push(item.quotePayload?.insuranceType);
  //       }
  //     });
  //     if (temporaryTitleList.length === 0) {
  //       temporaryTitleList = [InsuranceType.BUSINESS_INSURANCE];
  //     }
  //     temporaryTitleList = temporaryTitleList.map((key) => {
  //       if (key === InsuranceType.GOODS_IN_TRANSIT_INSURANCE) {
  //         return 'Goods in transit';
  //       } else {
  //         return 'Business insurance';
  //       }
  //     });

  //     if (!chartRef.current) {
  //       chartRef.current = generateChartInstance('payment-schedule-bar');
  //     }
  //     const yList = paymentScheduleInfo.schedule.map((item) => item.premium);

  //     // Virtual maximum
  //     let maxValue = parseInt((Number(max(yList)) * 1.2).toFixed(0));
  //     maxValue = roundToNearest(maxValue);

  //     drawChart(
  //       chartRef.current,
  //       paymentScheduleInfo.schedule.map((item) => item.dateText),
  //       yList,
  //       {
  //         legend: {
  //           data: [temporaryTitleList.join(', ')],
  //           orient: 'horizontal', // 水平布局
  //           bottom: 12, // 距离底部的距离，单位通常为像素
  //           left: '10%',
  //         },
  //         yAxis: {
  //           axisLabel: {
  //             show: true,
  //             textStyle: {
  //               color: '#999999',
  //             },
  //             formatter: function (value) {
  //               return `£${value}`;
  //             },
  //           },
  //           max: maxValue,
  //         },
  //         grid: { left: isMobile ? '15%' : '10%', top: '5%', right: '5%' },
  //         series: [
  //           {
  //             name: temporaryTitleList.join(', '),
  //             data: yList,
  //             type: 'bar',
  //             itemStyle: {
  //               color: '#6AE5BB',
  //               borderRadius: [3, 3, 0, 0],
  //             },
  //           },
  //         ],
  //       }
  //     );
  //   }
  // }, [paymentScheduleInfo]);

  useEffect(() => {
    if (paymentScheduleInfo) {
      // Temporary title code, to be removed
      let temporaryTitleList = [];
      policies.forEach((item) => {
        if (!temporaryTitleList.includes(item.quotePayload?.insuranceType)) {
          temporaryTitleList.push(item.quotePayload?.insuranceType);
        }
      });
      if (temporaryTitleList.length === 0) {
        temporaryTitleList = [InsuranceType.BUSINESS_INSURANCE];
      }
      temporaryTitleList = temporaryTitleList.map((key) => {
        if (key === InsuranceType.GOODS_IN_TRANSIT_INSURANCE) {
          return 'Goods in transit';
        } else {
          return 'Business insurance';
        }
      });

      if (!chartRef.current) {
        chartRef.current = generateChartInstance('payment-schedule-bar');
      }

      const schedules = paymentScheduleInfo.schedule || [];
      const activePremiumList = schedules.map((item) => item.active_premium);
      const pendingPremiumList = schedules.map((item) => item.pending_premium);

      // Virtual maximum
      const combineYList = [];
      activePremiumList.forEach((value, index) => {
        combineYList.push(
          Number(value || 0) + Number(pendingPremiumList[index] || 0)
        );
      });
      let maxValue = parseInt((Number(max(combineYList)) * 1.2).toFixed(0));
      maxValue = roundToNearest(maxValue);

      const series = [
        {
          name: 'Active premium',
          type: 'bar',
          stack: 'Ad',
          emphasis: {
            focus: 'series',
          },
          data: activePremiumList,
          itemStyle: {
            color: '#6AE5BB',
          },
        },
        {
          name: 'Pending premium',
          type: 'bar',
          stack: 'Ad',
          emphasis: {
            focus: 'series',
          },
          data: pendingPremiumList,
          itemStyle: {
            color: '#d6d6d6',
          },
        },
      ];

      drawStackedColumnChart(
        chartRef.current,
        schedules.map((item) => item.dateText),
        series,
        {
          yAxis: {
            axisLabel: {
              show: true,
              textStyle: {
                color: '#999999',
              },
              formatter: function (value) {
                return `£${value}`;
              },
            },
            max: maxValue,
          },
          grid: { left: isMobile ? '13%' : '8%', top: '5%', right: '5%' },
        }
      );
    }
  }, [paymentScheduleInfo]);

  /**
   * methods
   */
  const addAction = () => {
    setYear(year + 1);
    updatePaymentAllInfo({
      year: year + 1,
      queryUserId: queryParams?.queryUserId,
    });
  };

  const minusAction = () => {
    setYear(year - 1);
    updatePaymentAllInfo({
      year: year - 1,
      queryUserId: queryParams?.queryUserId,
    });
  };

  // policies is empty, not show
  if (!isArray(policies) || policies.length === 0) {
    return null;
  }

  return (
    <Box
      className={clsx('relative flex flex-col rounded-lg bg-white', className)}
    >
      {paymentLoading && <LogoLoading />}

      <Box className="flex flex-row items-center justify-between px-5 pt-5">
        <Typography className="text-black-secondary font-black" variant="h5">
          Payment schedule
        </Typography>
        {/* <Box className="flex flex-row items-center">
          <IconButton onClick={minusAction}>
            <ChevronLeftIcon className="h-5 w-5 text-[#C2C2C2]" />
          </IconButton>
          <Typography className="text-black-secondary font-bold" variant="subtitle2">
            {year}
          </Typography>
          <IconButton onClick={addAction}>
            <ChevronRightIcon className="h-5 w-5 text-[#C2C2C2]" />
          </IconButton>
        </Box> */}
      </Box>
      <Box id="payment-schedule-bar" className="mt-2 h-[320px] w-full"></Box>
    </Box>
  );
};
export default PaymentSchedule;
